import { WatchQueryFetchPolicy } from '@apollo/client';
import { useEffect, useRef, useState } from 'react';

import {
  Audit,
  Scalars,
  Task,
  useListActivitiesByTaskQuery,
} from '@graphql/generated';
import { isResultsTruncated } from '@utils/graphqlError';
/**
 * Retrieves a list of tasks
 */

const DEFAULT_PER_PAGE_COUNT = 10;

export const useListTaskActivityFromQuery = ({
  taskId = undefined,
  first = DEFAULT_PER_PAGE_COUNT,
  last = undefined,
  after = undefined,
  before = undefined,
  term = undefined,
  startDate = undefined,
  endDate = undefined,
  userIds = undefined,
  fetchPolicy = 'cache-and-network',
  onCompleted = undefined,
  onError = undefined,
  skip = false,
}: {
  taskId?: Task['id'];
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  term?: string;
  startDate?: Scalars['ISO8601DateTime']['input'];
  endDate?: Scalars['ISO8601DateTime']['input'];
  userIds?: string[];
  fetchPolicy?: WatchQueryFetchPolicy;
  onCompleted?: () => void;
  onError?: () => void;
  skip?: boolean;
}) => {
  const onCompletedRef = useRef(onCompleted);
  onCompletedRef.current = onCompleted;
  const onErrorRef = useRef(onError);
  onErrorRef.current = onError;

  const [refreshing, setRefreshing] = useState(false);
  const { data, loading, error, refetch, fetchMore } =
    useListActivitiesByTaskQuery({
      variables: {
        taskId: taskId as string,
        first,
        last,
        before,
        after,
        term,
        startDate,
        endDate,
        userIds,
      },
      fetchPolicy,
      errorPolicy: 'all',
      skip,
    });

  useEffect(() => {
    if (data) {
      onCompletedRef.current?.();
      setRefreshing(false);
    }
  }, [data]);

  useEffect(() => {
    if (error) {
      if (!isResultsTruncated(error)) onErrorRef.current?.();
      setRefreshing(false);
    }
  }, [error]);

  const fetchMoreFromCursor = () => {
    if (!Activities || !pageInfo?.hasNextPage || loading) return;
    const cursor = pageInfo.endCursor;
    fetchMore({
      variables: {
        after: cursor,
        first,
        term,
        startDate,
        endDate,
        userIds,
      },
    });
  };
  const { listActivitiesByTask } = data || {
    listActivitiesByTask: undefined,
  };

  const { edges = [], pageInfo } = listActivitiesByTask || { edges: [] };
  // TODO: This needs to be more properly typed to the returned graphql value.
  const Activities =
    (edges || []).reduce((acc, curr) => {
      if (curr?.node !== undefined) {
        return [...acc, curr.node as Audit];
      } else {
        return acc;
      }
    }, [] as Audit[]) || [];

  // pagination returns duplicate tasks when tasks are named the same resulting in duplicate key errors
  // can be removed when pagination is fixed
  const ActivityIds = Activities.map((t) => t.id);
  const filterOutDuplicates = Activities.filter((t, index) => {
    return !ActivityIds.includes(t.id.toString(), index + 1);
  });
  return {
    activity: filterOutDuplicates,
    loading,
    error,
    pageInfo,
    refetch,
    fetchMore,
    fetchMoreFromCursor,
    refreshing,
    setRefreshing,
    isTruncated: isResultsTruncated(error),
  };
};
